import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';
import { getCartProvinces } from '@theme/cart-provinces';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  /** @type {MutationObserver | null} */
  #termsObserver = null;

  /** @type {number} */
  #termsFrame = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.addEventListener('click', this.#handleInstallmentsClick, true);
    this.addEventListener('change', this.#handleTermsChange);
    this.addEventListener('change', this.#handleShippingCountryChange);
    this.#dialog?.addEventListener('click', this.#handlePanelToggle);

    this.#termsObserver = new MutationObserver(() => this.#scheduleTermsSync());
    this.#termsObserver.observe(this, { childList: true, subtree: true });
    this.#syncTerms();

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.removeEventListener('click', this.#handleInstallmentsClick, true);
    this.removeEventListener('change', this.#handleTermsChange);
    this.removeEventListener('change', this.#handleShippingCountryChange);
    this.#dialog?.removeEventListener('click', this.#handlePanelToggle);
    this.#termsObserver?.disconnect();
    this.#termsObserver = null;
    cancelAnimationFrame(this.#termsFrame);
  }

  /**
   * Toggles bottom-docked panels (note, shipping, coupon) open/closed.
   * Only one panel open at a time. Cancel buttons and clicks outside close all panels.
   * @param {Event} event
   */
  #handlePanelToggle = (event) => {
    const dialog = this.#dialog;
    if (!dialog) return;

    const panelTrigger = event.target instanceof Element ? event.target.closest('[data-panel]') : null;
    const closeBtn = event.target instanceof Element ? event.target.closest('[data-panel-close]') : null;
    const panelContent = event.target instanceof Element ? event.target.closest('.cart-drawer__panel') : null;

    // Close button clicked — close all panels
    if (closeBtn) {
      this.#closeAllPanels(dialog);
      return;
    }

    // Panel trigger button clicked — toggle that panel
    if (panelTrigger) {
      const panelId = panelTrigger.getAttribute('data-panel');
      const panel = dialog.querySelector(`.cart-drawer__panel[data-panel-id="${panelId}"]`);
      if (!panel) return;

      const isOpen = panel.classList.contains('cart-drawer__panel--open');
      this.#closeAllPanels(dialog);
      if (!isOpen) {
        panel.classList.add('cart-drawer__panel--open');
        panelTrigger.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    // Click inside an open panel (not on close button) — let it be
    if (panelContent) return;

    // Click outside any panel while one is open — close all panels
    if (dialog.querySelector('.cart-drawer__panel--open')) {
      this.#closeAllPanels(dialog);
    }
  };

  /** @param {HTMLDialogElement} dialog */
  #closeAllPanels(dialog) {
    dialog.querySelectorAll('.cart-drawer__panel--open').forEach((panel) => {
      panel.classList.remove('cart-drawer__panel--open');
    });
    dialog.querySelectorAll('.cart-drawer__tool[aria-expanded="true"]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });
  }

  /**
   * Populates the province dropdown based on the selected country.
   * Only shown for United States and United Kingdom; hidden for all other countries.
   * @param {Event} event
   */
  #handleShippingCountryChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement) || event.target.id !== 'cart-shipping-country') return;
    this.#syncProvinces();
  };

  /** Reads the current country value and populates or hides the province field. */
  #syncProvinces() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const countrySelect = dialog.querySelector('#cart-shipping-country');
    const provinceSelect = dialog.querySelector('#cart-shipping-province');
    const provinceField = provinceSelect?.closest('.cart-drawer__panel-field');
    if (!countrySelect || !provinceSelect || !provinceField) return;

    const provinces = getCartProvinces(countrySelect.value);

    if (provinces.length === 0) {
      provinceField.hidden = true;
      return;
    }

    provinceField.hidden = false;

    // Only rebuild if the options are different (avoids wiping the user's selection on re-renders)
    const currentCount = provinceSelect.options.length;
    const expectedCount = provinces.length + 1; // +1 for the placeholder
    if (currentCount === expectedCount) return;

    provinceSelect.innerHTML = '<option value="">Select province</option>';
    for (const name of provinces) {
      const option = document.createElement('option');
      option.value = name;
      option.textContent = name;
      provinceSelect.append(option);
    }
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => {
      this.#updateStickyState();
      this.#syncTerms();
      this.#syncProvinces();
    });
  };

  #scheduleTermsSync() {
    cancelAnimationFrame(this.#termsFrame);
    this.#termsFrame = requestAnimationFrame(() => this.#syncTerms());
  }

  /**
   * Keeps checkout disabled until the shopper accepts the terms.
   * The choice is remembered for the tab so cart morphs do not clear it.
   */
  #syncTerms() {
    const box = this.querySelector('.cart-drawer__terms-input');
    const checkout = this.querySelector('#checkout');
    if (!(box instanceof HTMLInputElement) || !(checkout instanceof HTMLButtonElement)) return;

    let accepted = false;
    try {
      accepted = sessionStorage.getItem('cart-terms-accepted') === '1';
    } catch {
      accepted = box.checked;
    }

    if (accepted !== box.checked) box.checked = accepted;
    checkout.disabled = !box.checked;
  }

  /**
   * @param {Event} event
   */
  #handleTermsChange = (event) => {
    const box = event.target;
    if (!(box instanceof HTMLInputElement) || !box.classList.contains('cart-drawer__terms-input')) return;

    try {
      sessionStorage.setItem('cart-terms-accepted', box.checked ? '1' : '0');
    } catch {
      // Storage can be blocked; the checkbox state still gates this view.
    }

    const checkout = this.querySelector('#checkout');
    if (checkout instanceof HTMLButtonElement) checkout.disabled = !box.checked;
  };

  /** Handles the theme-drawer opening by updating its sticky state. */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#syncProvinces();
  };

  /**
   * Closes the cart drawer before the installments dialog opens.
   *
   * The payment-terms element replaces its shadow content when cart totals change,
   * so listen on this stable ancestor instead of binding to a particular CTA instance.
   *
   * @param {MouseEvent} event
   */
  #handleInstallmentsClick = (event) => {
    const path = event.composedPath();
    const ctaIndex = path.findIndex(
      (element) => element instanceof Element && element.id === 'shopify-installments-cta'
    );
    if (ctaIndex === -1) return;

    const ctaRoot = path.slice(ctaIndex + 1).find((element) => element instanceof ShadowRoot);
    if (!ctaRoot?.host.matches('shopify-payment-terms')) return;

    this.#themeDrawer?.close();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

const recommendationCache = new Map();

/**
 * @param {number} cents
 * @param {string} format
 */
function formatMoney(cents, format) {
  const amount = (cents / 100).toFixed(2);
  const [whole, fraction] = amount.split('.');
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const value = `${withCommas}.${fraction}`;
  const commaValue = `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${fraction}`;
  const formatted = (format || 'Rs.{{amount}}')
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, commaValue)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withCommas)
    .replace(/\{\{\s*amount\s*\}\}/g, value);

  return formatted.includes('{{') ? `Rs.${value}` : formatted;
}

/**
 * @param {Record<string, unknown>} product
 */
function recommendationPriceCents(product) {
  if (typeof product.price === 'number') return product.price;
  const variants = /** @type {Array<Record<string, unknown>> | undefined} */ (product.variants);
  const variantPrice = variants?.[0]?.price;
  if (typeof variantPrice === 'number') return variantPrice;
  if (typeof variantPrice === 'string') return Math.round(Number.parseFloat(variantPrice) * 100);
  return 0;
}

/**
 * @param {Record<string, unknown>} product
 */
function recommendationImage(product) {
  const image = product.featured_image;
  if (typeof image === 'string') return image;
  if (image && typeof image === 'object') {
    const record = /** @type {Record<string, unknown>} */ (image);
    if (typeof record.src === 'string') return record.src;
    if (typeof record.url === 'string') return record.url;
  }
  const images = /** @type {unknown[] | undefined} */ (product.images);
  const first = images?.[0];
  if (typeof first === 'string') return first;
  if (first && typeof first === 'object' && typeof /** @type {Record<string, unknown>} */ (first).src === 'string') {
    return /** @type {Record<string, string>} */ (first).src;
  }
  return '';
}

class CartDrawerRecommendations extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {number} */
  #index = 0;

  /** @type {Array<Record<string, unknown>>} */
  #products = [];

  connectedCallback() {
    this.#load();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  async #load() {
    const productId = this.dataset.productId;
    if (!productId) return;

    const cached = recommendationCache.get(productId);
    if (cached) {
      this.#products = cached;
      this.#index = 0;
      this.#render();
      return;
    }

    this.#abort?.abort();
    const controller = new AbortController();
    this.#abort = controller;

    try {
      const url = new URL(this.dataset.url || '/recommendations/products.json', window.location.origin);
      url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', this.dataset.limit || '8');
      url.searchParams.set('intent', 'related');

      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      const data = response.ok ? await response.json() : { products: [] };
      let products = Array.isArray(data.products) ? data.products : [];
      products = products.filter((product) => String(product.id) !== productId);

      if (!products.length && this.dataset.fallbackUrl) {
        const fallbackUrl = new URL(this.dataset.fallbackUrl, window.location.origin);
        fallbackUrl.searchParams.set('limit', this.dataset.limit || '8');
        const fallback = await fetch(fallbackUrl, { signal: controller.signal, credentials: 'same-origin' });
        if (fallback.ok) {
          const catalog = await fallback.json();
          products = (Array.isArray(catalog.products) ? catalog.products : []).filter(
            (product) => String(product.id) !== productId
          );
        }
      }

      if (controller.signal.aborted || !this.isConnected) return;
      recommendationCache.set(productId, products);
      this.#products = products;
      this.#index = 0;
      this.#render();
    } catch (error) {
      if (error?.name !== 'AbortError') this.hidden = true;
    }
  }

  #render() {
    if (!this.#products.length) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    const root = this.shadowRoot || this.attachShadow({ mode: 'open' });

    // If already built, just sync the active state without rebuilding
    if (root.querySelector('.wrap')) {
      this.#syncSlider();
      return;
    }

    const style = document.createElement('style');
    style.textContent = `
      :host { display: block; width: 100%; color: #878787; font-family: Poppins, sans-serif; font-size: 14px; }

      /* Outer card: rounded, shadow */
      .wrap {
        margin: 0 0 4px;
        border-radius: 5px;
        overflow: hidden;
        box-shadow: 0 0 3px 0 rgb(0 0 0 / 20%);
        background: #fff;
        color: #222;
      }

      /* Grey header bar */
      .heading {
        background-color: #f5f5f5;
        font-size: 13px;
        font-weight: 500;
        padding: 8px 10px;
        color: #222;
        text-align: center;
        margin: 0;
        letter-spacing: 0.02em;
      }

      /* Slider viewport — clips overflow */
      .slider-viewport {
        overflow: hidden;
        width: 100%;
      }

      /* Track that holds all cards side by side */
      .slider-track {
        display: flex;
        width: 100%;
        transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        will-change: transform;
      }

      /* Each product card */
      .card {
        flex: 0 0 100%;
        width: 100%;
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 8px 10px;
        text-decoration: none;
        color: inherit;
        box-sizing: border-box;
      }

      /* Thumbnail */
      .thumb {
        flex: 0 0 auto;
        width: 80px;
        height: 110px;
        overflow: hidden;
        border-radius: 2px;
        background: #f5f5f5;
      }
      .thumb img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        display: block;
      }

      /* Text column */
      .copy { flex: 1 1 0; min-width: 0; }
      .name {
        display: block;
        margin: 0 0 3px;
        font-size: 13px;
        font-weight: 500;
        line-height: 1.35;
        color: #222;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .price { display: block; margin: 0; font-size: 13px; color: #000; }

      /* Navigation bar: prev · dots · next */
      .nav {
        display: grid;
        grid-template-columns: 40px 1fr 40px;
        align-items: center;
        border-top: 1px solid #ddd;
        min-height: 38px;
      }

      /* Arrow buttons */
      .nav-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 38px;
        padding: 0;
        border: 0;
        background: transparent;
        color: #222;
        cursor: pointer;
        transition: color .2s;
      }
      .nav-btn:hover { color: #000; }
      .nav-btn svg { width: 12px; height: 12px; stroke: currentColor; fill: currentColor; stroke-width: 3; }

      /* Dots */
      .dots {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        border-inline: 1px solid #ddd;
        padding: 6px 0;
      }
      .dot {
        width: 9px;
        height: 9px;
        padding: 0;
        border: 2px solid rgb(0 0 0 / 20%);
        border-radius: 50%;
        background: #fff;
        cursor: pointer;
        transition: background .2s, border-color .2s;
      }
      .dot[aria-current='true'] { background: #222; border-color: #222; }
    `;

    // Build all cards up front
    const heading = document.createElement('p');
    heading.className = 'heading';
    heading.textContent = this.dataset.label || 'You may also like';

    const viewport = document.createElement('div');
    viewport.className = 'slider-viewport';

    const track = document.createElement('div');
    track.className = 'slider-track';

    for (const product of this.#products) {
      const productTitle = String(product.title || '');
      const productHandle = String(product.handle || '');
      const productHref = typeof product.url === 'string' ? product.url : `/products/${productHandle}`;
      const productImage = recommendationImage(product);
      const productPrice = formatMoney(recommendationPriceCents(product), this.dataset.moneyFormat || '');

      const card = document.createElement('a');
      card.className = 'card';
      card.href = productHref;

      if (productImage) {
        const thumb = document.createElement('div');
        thumb.className = 'thumb';
        const img = document.createElement('img');
        img.alt = productTitle;
        img.src = productImage.includes('?') ? `${productImage}&width=240` : `${productImage}?width=240`;
        img.width = 80;
        img.height = 110;
        img.loading = 'lazy';
        thumb.append(img);
        card.append(thumb);
      }

      const copy = document.createElement('div');
      copy.className = 'copy';
      const name = document.createElement('span');
      name.className = 'name';
      name.textContent = productTitle;
      const priceEl = document.createElement('span');
      priceEl.className = 'price';
      priceEl.textContent = productPrice;
      copy.append(name, priceEl);
      card.append(copy);
      track.append(card);
    }

    viewport.append(track);

    // Build nav
    const nav = document.createElement('div');
    nav.className = 'nav';

    const previous = document.createElement('button');
    previous.type = 'button';
    previous.className = 'nav-btn';
    previous.setAttribute('aria-label', this.dataset.previousLabel || 'Previous');
    previous.innerHTML = `<svg viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,95 L 20,50 L 65,5 L 60,0 Z"/></svg>`;
    previous.addEventListener('click', () => this.#step(-1));

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'nav-btn';
    next.setAttribute('aria-label', this.dataset.nextLabel || 'Next');
    next.innerHTML = `<svg viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,95 L 20,50 L 65,5 L 60,0 Z" transform="translate(100,100) rotate(180)"/></svg>`;
    next.addEventListener('click', () => this.#step(1));

    const dots = document.createElement('div');
    dots.className = 'dots';
    this.#products.forEach((item, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'dot';
      dot.setAttribute('aria-label', String(item.title || `Product ${index + 1}`));
      if (index === this.#index) dot.setAttribute('aria-current', 'true');
      dot.addEventListener('click', () => {
        this.#index = index;
        this.#syncSlider();
      });
      dots.append(dot);
    });

    nav.append(previous, dots, next);

    const wrap = document.createElement('div');
    wrap.className = 'wrap';
    wrap.append(heading, viewport, nav);
    root.append(style, wrap);

    // Set initial position without transition
    this.#syncSlider(false);
  }

  /** Slide the track to the current index and update dots. */
  #syncSlider(animate = true) {
    const root = this.shadowRoot;
    if (!root) return;

    const track = root.querySelector('.slider-track');
    if (track instanceof HTMLElement) {
      if (!animate) track.style.transition = 'none';
      track.style.transform = `translateX(-${this.#index * 100}%)`;
      if (!animate) {
        // Force reflow then restore transition
        track.getBoundingClientRect();
        track.style.transition = '';
      }
    }

    root.querySelectorAll('.dot').forEach((dot, i) => {
      dot.setAttribute('aria-current', String(i === this.#index));
    });
  }

  /**
   * @param {number} direction
   */
  #step(direction) {
    const count = this.#products.length;
    if (!count) return;
    this.#index = (this.#index + direction + count) % count;
    this.#syncSlider();
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
